import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Sidebar } from "@/components/layout/sidebar";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import { FAQ } from "@/lib/user-types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "wouter";

export default function FaqPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [openItems, setOpenItems] = useState<string[]>([]);
  
  const { data: faqs, isLoading } = useQuery<FAQ[]>({
    queryKey: ["/api/faqs"],
  });

  const filteredFaqs = faqs?.filter((faq) => {
    return (
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Auto-expand items that match the search
  useEffect(() => {
    if (searchQuery && filteredFaqs) {
      setOpenItems(filteredFaqs.map(faq => `item-${faq.id}`));
    }
  }, [searchQuery, filteredFaqs]);
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {user ? <Sidebar /> : <Header />}
      
      <div className={`flex-1 ${user ? "ml-0 md:ml-64" : ""}`}>
        <main className="py-6">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-semibold text-gray-900 font-serif">Frequently Asked Questions</h1>
              <p className="mt-2 text-gray-500">
                Find answers to common questions about PreVisly
              </p>
            </div>
            
            <div className="relative mb-8">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search questions..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((index) => (
                  <FaqSkeleton key={index} />
                ))}
              </div>
            ) : filteredFaqs && filteredFaqs.length > 0 ? (
              <Accordion
                type="multiple"
                value={openItems}
                onValueChange={setOpenItems}
                className="space-y-4"
              >
                {filteredFaqs.map((faq) => (
                  <AccordionItem 
                    key={faq.id} 
                    value={`item-${faq.id}`}
                    className="border rounded-lg overflow-hidden bg-white shadow-sm"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline">
                      <span className="text-lg font-medium text-left">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 pt-2 text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-500">No FAQ items match your search.</p>
              </div>
            )}

            <div className="mt-12 text-center">
              <p className="text-gray-500">
                Still have questions? <Link href="/" className="text-primary font-medium hover:underline">Contact our support team</Link>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function FaqSkeleton() {
  return (
    <div className="border rounded-lg bg-white shadow-sm p-4">
      <div className="flex justify-between items-center">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-5 w-5 rounded-full" />
      </div>
    </div>
  );
}
